import { db } from './indexeddb'
import type { Task } from './models'
import type { Setting } from './types'

/**
 * Query Helpers for Database Operations
 * Provides abstraction layer for common database queries
 */

// ==================== TASKS ====================

/**
 * Create a new task (delegates to db.createTask)
 */
export async function createTask(
  task: {
    title: string
    completed: boolean
    description?: string
    userId?: string
    lifeSpace: 'personal' | 'work' | 'home'
  },
): Promise<string> {
  const created = await db.createTask({
    title: task.title,
    description: task.description || null,
    status: task.completed ? 'completed' : 'todo',
    attention: 'medium',
    category: 'other',
    lifeSpace: task.lifeSpace,
    dueDate: null,
    completedAt: task.completed ? new Date() : null,
    estimatedDuration: null,
    actualDuration: null,
    tags: [],
    parentTaskId: null,
    pausedAt: null,
  })
  return created.id
}

/**
 * Get task by ID
 */
export async function getTaskById(id: string): Promise<Task | undefined> {
  const task = await db.tasks.get(id)
  return task || undefined
}

/**
 * Get all tasks (with optional filters)
 */
export async function getTasks(filters?: {
  completed?: boolean
  userId?: string
}): Promise<Task[]> {
  let tasks: Task[]

  if (filters?.userId) {
    let collection = db.tasks.where('userId').equals(filters.userId)
    if (filters?.completed !== undefined) {
      const isCompleted = filters.completed
      collection = collection.and((task) =>
        isCompleted ? task.status === 'completed' : task.status !== 'completed'
      )
    }
    tasks = await collection.toArray()
  } else if (filters?.completed !== undefined) {
    const isCompleted = filters.completed
    tasks = await db.tasks
      .filter((task) =>
        isCompleted ? task.status === 'completed' : task.status !== 'completed'
      )
      .toArray()
  } else {
    tasks = await db.tasks.toArray()
  }

  // Sort by createdAt descending
  return tasks.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
}

/**
 * Update a task
 */
export async function updateTask(
  id: string,
  updates: Partial<Omit<Task, 'id' | 'createdAt'>>
): Promise<number> {
  return db.tasks.update(id, {
    ...updates,
    updatedAt: new Date(),
  })
}

/**
 * Delete a task (soft delete)
 */
export async function deleteTask(id: string): Promise<void> {
  await db.deleteTask(id)
}

/**
 * Hard delete a task
 */
export async function hardDeleteTask(id: string): Promise<void> {
  await db.tasks.delete(id)
}

/**
 * Toggle task completion
 */
export async function toggleTaskCompletion(id: string): Promise<void> {
  const task = await getTaskById(id)
  if (task) {
    const newStatus = task.status === 'completed' ? 'todo' : 'completed'
    await updateTask(id, {
      status: newStatus,
      completedAt: newStatus === 'completed' ? new Date() : null,
    })
  }
}

// ==================== SETTINGS ====================

/**
 * Get setting by key
 */
export async function getSetting(key: string, _userId?: string): Promise<any> {
  return db.getSettingValue(key)
}

/**
 * Set a setting
 */
export async function setSetting(
  key: string,
  value: any,
  _userId?: string
): Promise<void> {
  await db.setSetting(key, value)
}

/**
 * Get all settings
 */
export async function getAllSettings(_userId?: string): Promise<Setting[]> {
  const settings = await db.getAllSettings()
  // Map to legacy Setting format for compatibility
  return settings.map(s => ({
    id: undefined as any,
    key: s.key,
    value: s.value,
    updatedAt: s.updatedAt,
  }))
}

/**
 * Delete a setting
 */
export async function deleteSetting(key: string, _userId?: string): Promise<void> {
  const setting = await db.getSetting(key)
  if (setting) {
    await db.settings.delete(setting.id)
  }
}

// ==================== SYNC QUEUE ====================

/**
 * Add item to sync queue
 */
export async function addToSyncQueue(
  action: 'create' | 'update' | 'delete',
  table: 'tasks' | 'settings' | 'projects' | 'tags',
  recordId: number,
  data: any
): Promise<number> {
  return db.syncQueue.add({
    action,
    table,
    recordId,
    data,
    synced: false,
    timestamp: new Date(),
  }) as Promise<number>
}

/**
 * Get pending sync items
 */
export async function getPendingSyncItems() {
  return db.syncQueue.filter(item => !item.synced).toArray()
}

/**
 * Mark sync item as synced
 */
export async function markSyncItemAsSynced(id: number): Promise<void> {
  await db.syncQueue.update(id, { synced: true })
}

/**
 * Clear synced items (cleanup old queue entries)
 */
export async function clearSyncedItems(): Promise<void> {
  await db.syncQueue.filter(item => item.synced === true).delete()
}

// ==================== UTILITIES ====================

/**
 * Get tasks statistics
 */
export async function getTasksStats() {
  const [total, completed] = await Promise.all([
    db.tasks.count(),
    db.tasks.filter((t) => t.status === 'completed').count(),
  ])
  return {
    total,
    completed,
    pending: total - completed,
  }
}
